// src/screens/mypage/FavoritesScreen.js
// 47차: 이벤트 알림 토글 정상 동작 확인
//       — load() 에서 favId를 반드시 normalizeProduct 결과에 포함시키도록 수정
//       — handleToggleNotify 에 favId가 항상 전달되도록 수정
// 51차: 색 theme.js 단일화 — COLORS 참조 (의미색 없음, 골드/회색만)
// 71차: 알림 토글 라벨 "이벤트 알림" → "PICK 알림" (70차 "이벤트"→"PICK 테마" 명칭 변경 누락분)
// 74차: 화면 자체 커스텀 헤더 → 공통 AppHeader로 교체
// 77차: AppHeader에 제목이 없어 "‹ 뒤로"만 보이는 화면들이 여러 번 "AppHeader 없음"으로
// 신고됐음 — 실제론 AppHeader가 맞지만 centerElement(제목)를 아무도 안 넘겨서 매번
// 뒤로가기만 있는 빈 헤더로 보이던 것. 제목 추가.
// 77차: 빈 즐겨찾기 화면의 "테마 둘러보기" 버튼이 navigation.navigate('Booking')을 호출해
// NAVIGATION 에러 발생 — 이 화면은 Main 탭 네비게이터 밖(최상위 Stack)에 있어서 Booking
// 탭 이름만으로는 못 찾음. navigate('Main',{screen:'Booking'})으로 수정.
// 78차: "테마 둘러보기" 버튼을 공용 SecondaryButton으로 교체, Switch trackColor의
// 하드코딩 색상(#2a2a3a)을 COLORS.surface2로 정리(색상 하드코딩 금지 컨벤션).

import React, { useState, useEffect, useCallback } from 'react';
import { showAlert } from '../../components/common/AppAlert';
import {
  View, Text, FlatList, TouchableOpacity, StyleSheet,
  RefreshControl, Switch,
} from 'react-native';
// 78차 37차: 썸네일 캐싱 없이 매번 재다운로드되던 것 — expo-image로 교체(디스크 캐시 자동)
import { Image } from 'expo-image';
import {
  collection, query, where, getDocs, deleteDoc, doc, updateDoc,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { getProductById, normalizeProduct } from '../../services/productService';
import { useAuth } from '../../contexts/AuthContext';
import LoadingOverlay from '../../components/common/LoadingOverlay';
import AppHeader, { LogoMark } from '../../components/common/AppHeader';
import { SecondaryButton } from '../../components/common/Buttons';
import { EmptyFavoritesIcon, MenuStoreIcon, IconLabel, MenuBellIcon, MenuFavoriteIcon } from '../../components/common/Icons';
import { COLORS } from '../../constants/theme';
import { getSeatingTypeLabel } from '../../constants';

const GOLD     = COLORS.gold;
const BG       = COLORS.bg;
const SURFACE  = COLORS.surface;
const BORDER   = COLORS.border;
const TEXT     = COLORS.text;
const TEXT_SEC = COLORS.textSec;
const TEXT_MUTED = COLORS.textMuted;

export default function FavoritesScreen({ navigation }) {
  const { user } = useAuth();
  const [favorites,  setFavorites]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError,  setLoadError]  = useState(false);

  const load = useCallback(async () => {
    if (!user?.uid) { setLoading(false); return; }
    try {
      setLoadError(false);
      const snap = await getDocs(
        query(collection(db, 'favorites'), where('uid', '==', user.uid))
      );
      // favId, notify 같이 포함
      const favDocs = snap.docs.map(d => ({
        favId: d.id,
        notify: d.data().notify ?? false,
        productId: d.data().productId,
      }));

      if (favDocs.length === 0) { setFavorites([]); return; }

      const results = await Promise.allSettled(
        favDocs.map(async (fav) => {
          if (!fav.productId) return null;
          const product = await getProductById(fav.productId);
          if (!product) return null;
          // 47차: favId, notify 항상 유지
          return {
            ...normalizeProduct(product),
            favId: fav.favId,
            notify: fav.notify,
          };
        })
      );

      setFavorites(
        results
          .filter(r => r.status === 'fulfilled' && r.value !== null)
          .map(r => r.value)
      );
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user?.uid]);

  useEffect(() => { load(); }, [load]);

  const handleRemove = (favId, name) => {
    showAlert('즐겨찾기 해제', `${name}을(를) 즐겨찾기에서 삭제할까요?`, [
      { text: '취소', style: 'cancel' },
      {
        text: '삭제', style: 'destructive',
        onPress: async () => {
          try {
            await deleteDoc(doc(db, 'favorites', favId));
            setFavorites(prev => prev.filter(f => f.favId !== favId));
          } catch {
            showAlert('오류', '삭제에 실패했습니다.');
          }
        },
      },
    ]);
  };

  const handleToggleNotify = async (favId, current) => {
    if (!favId) {
      showAlert('오류', '즐겨찾기 정보가 올바르지 않습니다.\n새로고침 후 다시 시도해주세요.');
      return;
    }
    try {
      await updateDoc(doc(db, 'favorites', favId), { notify: !current });
      setFavorites(prev =>
        prev.map(f => f.favId === favId ? { ...f, notify: !current } : f)
      );
    } catch {
      showAlert('오류', '알림 설정 변경에 실패했습니다.');
    }
  };

  if (loading) return <LoadingOverlay />;

  return (
    <View style={s.container}>
      <AppHeader showBack centerElement={<Text style={{ color: TEXT, fontSize: 15, fontWeight: '800' }}>즐겨찾기</Text>} />

      <FlatList
        data={favorites}
        keyExtractor={i => i.favId}
        contentContainerStyle={{ padding: 16, paddingBottom: 40 }}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            tintColor={GOLD}
            onRefresh={() => { setRefreshing(true); load(); }}
          />
        }
        ListEmptyComponent={
          loadError ? (
            <View style={s.empty}>
              <EmptyFavoritesIcon size={64} />
              <Text style={[s.emptyTitle, { marginTop: 14 }]}>즐겨찾기를 불러오지 못했어요</Text>
              <Text style={s.emptySub}>네트워크 상태를 확인하고 다시 시도해주세요.</Text>
              <SecondaryButton label="다시 시도" onPress={load} />
            </View>
          ) : (
            <View style={s.empty}>
              <EmptyFavoritesIcon size={64} />
              <Text style={[s.emptyTitle, { marginTop: 14 }]}>즐겨찾기한 테마가 없어요</Text>
              <Text style={s.emptySub}>예약 탭에서 ♡를 눌러 저장해보세요!</Text>
              <SecondaryButton
                label="테마 둘러보기 →"
                onPress={() => navigation.navigate('Main', { screen: 'Booking' })}
              />
            </View>
          )
        }
        renderItem={({ item }) => (
          <TouchableOpacity
            style={s.card}
            activeOpacity={0.85}
            onPress={() => navigation.navigate('BookingDetail', { product: item })}
          >
            <View style={s.imgWrap}>
              {item.thumbnailUrl
                ? <Image source={{ uri: item.thumbnailUrl }} style={s.img} />
                : <View style={[s.img, s.imgPlaceholder]}>
                    <LogoMark size={28} />
                  </View>
              }
            </View>

            <View style={s.info}>
              <Text style={s.name} numberOfLines={1}>{item.name}</Text>
              <IconLabel Icon={MenuStoreIcon} size={11} color={TEXT_SEC} textStyle={s.store} numberOfLines={1}>{item.storeName}</IconLabel>
              <View style={{ flexDirection: 'row', gap: 5, marginTop: 4, flexWrap: 'wrap' }}>
                {/* 80차 후속: [정책 확정] 배지 순서 — ①형식(방탈출/체험형, 필수) → ②19금 → ③장르 */}
                {item.bookingFormat === 'show' ? (
                  <View style={[s.tag, s.tagGray]}>
                    <Text style={[s.tagText, { color: TEXT_SEC }]}>체험형</Text>
                  </View>
                ) : (
                  <View style={[s.tag, { backgroundColor: COLORS.teal+'22', borderColor: COLORS.teal+'55' }]}>
                    <Text style={[s.tagText, { color: COLORS.teal }]}>방탈출</Text>
                  </View>
                )}
                {/* 80차 후속: 체험형 전용 — 단독 대관/합석 뱃지 */}
                {item.bookingFormat === 'show' && (
                  <View style={[s.tag, s.tagGray]}>
                    <Text style={[s.tagText, { color: TEXT_SEC }]}>{getSeatingTypeLabel(item.seatingType)}</Text>
                  </View>
                )}
                {item.isAdultOnly && (
                  <View style={[s.tag, { backgroundColor: COLORS.dangerBg, borderColor: COLORS.danger+'55' }]}>
                    <Text style={[s.tagText, { color: COLORS.danger }]}>19금</Text>
                  </View>
                )}
                {item.genres?.[0] && (
                  <View style={s.tag}>
                    <Text style={s.tagText}>{item.genres[0]}</Text>
                  </View>
                )}
                {item.bookingFormat !== 'show' && item.difficulty && (
                  <View style={[s.tag, s.tagGray]}>
                    <Text style={[s.tagText, { color: TEXT_SEC }]}>{item.difficulty}</Text>
                  </View>
                )}
              </View>
              {/* 80차 후속: 체험형(정원제)은 1인당 단가로 표기 */}
              {item.bookingFormat === 'show' ? (
                item.pricePerPerson > 0 && (
                  <Text style={s.price}>{Number(item.pricePerPerson).toLocaleString()}원 (1인당)</Text>
                )
              ) : (
                item.price != null && (
                  <Text style={s.price}>{Number(item.price).toLocaleString()}원~</Text>
                )
              )}
              {/* 78차: "PICK 알림"(신규 PICK 등록만 대상)이던 라벨을 넓힘 — 같은 notify
                  플래그를 재개장/가격변경 알림에도 그대로 재사용(onStoreUpdated, functions/index.js)
                  하게 되어, 토글이 실제로 하는 일을 더 정확히 알려주도록 문구만 변경 */}
              <TouchableOpacity
                style={s.notifyRow}
                onPress={() => handleToggleNotify(item.favId, !!item.notify)}
                activeOpacity={0.7}>
                <IconLabel Icon={MenuBellIcon} size={13} color={item.notify ? GOLD : (s.notifyLabel.color || '#777')} textStyle={s.notifyLabel} numberOfLines={1}>
                  소식 알림
                </IconLabel>
                <Switch
                  value={!!item.notify}
                  onValueChange={() => handleToggleNotify(item.favId, !!item.notify)}
                  trackColor={{ false: COLORS.surface2, true: GOLD + '60' }}
                  thumbColor={item.notify ? GOLD : '#555'}
                  style={{ transform: [{ scaleX: 0.8 }, { scaleY: 0.8 }] }}
                />
              </TouchableOpacity>
            </View>

            <TouchableOpacity
              style={s.heartBtn}
              onPress={() => handleRemove(item.favId, item.name)}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
              <MenuFavoriteIcon size={24} color={GOLD} />
            </TouchableOpacity>
          </TouchableOpacity>
        )}
      />
    </View>
  );
}

const s = StyleSheet.create({
  container:   { flex: 1, backgroundColor: BG },

  card:        { flexDirection: 'row', alignItems: 'center', backgroundColor: SURFACE,
                 borderRadius: 14, padding: 12, marginBottom: 10,
                 borderWidth: 1, borderColor: BORDER,
                 shadowColor: '#000', shadowOffset: { width: 0, height: 2 },
                 shadowOpacity: 0.3, shadowRadius: 6, elevation: 4 },
  imgWrap:     { marginRight: 12 },
  img:         { width: 76, height: 76, borderRadius: 10, backgroundColor: COLORS.surface2 },
  imgPlaceholder: { justifyContent: 'center', alignItems: 'center' },
  info:        { flex: 1 },
  name:        { color: TEXT, fontSize: 14, fontWeight: '700', marginBottom: 3 },
  store:       { color: TEXT_SEC, fontSize: 12, marginBottom: 2 },
  tag:         { alignSelf: 'flex-start', backgroundColor: GOLD + '18', borderRadius: 5,
                 paddingHorizontal: 8, paddingVertical: 2,
                 borderWidth: 1, borderColor: GOLD + '44' },
  tagGray:     { backgroundColor: 'rgba(255,255,255,0.05)', borderColor: BORDER },
  tagText:     { color: GOLD, fontSize: 11, fontWeight: '600' },
  price:       { color: GOLD, fontSize: 12, fontWeight: '800', marginTop: 5 },
  heartBtn:    { padding: 6 },

  empty:       { alignItems: 'center', paddingTop: 100 },
  emptyTitle:  { color: TEXT, fontSize: 16, fontWeight: '800', marginBottom: 6 },
  emptySub:    { color: TEXT_MUTED, fontSize: 13, marginBottom: 24, textAlign: 'center' },

  notifyRow:   { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
                 marginTop: 8, paddingTop: 8,
                 borderTopWidth: 1, borderTopColor: 'rgba(212,168,67,0.1)' },
  notifyLabel: { color: TEXT_MUTED, fontSize: 11, fontWeight: '600' },
});